// Make POs (ERP-PLAN §5.11): the parts of a build whose documents its CM gets with the make PO, beside the built item's
// own spec. A PCBA the CM builds inside the build (a phantom sub-assembly) carries the Gerbers, BOM + placement and
// firmware; a box or PCB the CM buys itself carries what it's printed or made from. What HHL sends arrives finished,
// a service has no spec, and an HHL product label comes as the labels HHL Ops makes (shared/product-labels.ts); a bag
// or box carrying one is still listed (the CM buys it and sticks the label on).
// Each sent line pins these parts' spec revisions (po_line.part_specs) like its own spec_revision_id: the server at
// "Mark as sent" (server/src/mutations/po-docs.ts), the browser to show a draft's (web/src/pages/po/docs.ts).
import { type BomIndex, type BomItem, type BomRow, byCm, resolve, type Supply } from "./boms.ts";
import { isLabelOnly } from "./product-labels.ts";

/** A build part a sent line carries: its item and the spec revision the CM gets (null: none was released when pinned). */
export type PartSpec = { item_id: string; spec_revision_id: string | null };

/**
 * The parts of `item` built from `bom` (a make order's revision) whose documents the CM gets: phantom sub-assemblies it
 * makes in the build and parts it buys or makes itself (a "CM buys" or "CM makes" line, or anything under a phantom on a
 * "CM buys" line), in BOM order,
 * each once.
 */
export function buildDocParts(ix: BomIndex, bom: BomRow, item: BomItem): BomItem[] {
  const out = new Map<string, BomItem>();
  for (const l of resolve(ix, bom, bom.shopify_product_id ? item : undefined).lines) {
    if (!l.phantom && !byCm(l.supply)) continue;
    if (l.item.kind === "service" || isLabelOnly(l.item) || l.item.id === item.id) continue;
    out.set(l.item.id, l.item);
  }
  return [...out.values()];
}

/** One row of what goes into each unit of a build, as its CM reads it. */
export type BuildPartRow = {
  key: string;
  /** 0 for the build's own lines; a part of a sub-assembly built in the same order is one deeper. */
  depth: number;
  itemId: string;
  sku: string;
  name: string;
  /** How many go into one finished unit, the scrap allowance in, through every sub-assembly built inside. */
  perUnit: number;
  supply: Supply;
  /** A sub-assembly built in the same order: a row of its own, its parts under it. */
  inside: boolean;
  packaging: boolean;
  /** The CM building it, by name (Elecrow), for the words: "Elecrow makes it". */
  cm: string;
  /** A part HHL sends: the vendor it comes from (its preferred one), "Hand Held Legend sends it (from Favor Union)". */
  from: string | null;
};

/**
 * What goes into each unit of `item` built from `bom` (a make order's revision), for its CM (Mitch, 2026-11-04: the CM
 * must see "the actual BOMs"; a build page that never said what HHL sends was "unacceptable"): every part in BOM order,
 * a sub-assembly built in the same order as a row with its parts under it, how many per unit and who supplies it, the
 * run's word on a part's supply where it says (`supplyOf`, as the make order's own snapshot: makeComponents()).
 * Services are work, not parts, so they're left out; an HHL product label stays (it goes on the unit).
 */
export function buildPartsList(
  ix: BomIndex,
  bom: BomRow,
  item: BomItem,
  supplyOf?: (itemId: string) => Supply | undefined,
  names: { cm: string; fromOf?: (itemId: string) => string | null } = { cm: "" },
): BuildPartRow[] {
  return resolve(ix, bom, bom.shopify_product_id ? item : undefined)
    .lines.filter((l) => l.item.kind !== "service" && l.item.id !== item.id)
    .map((l) => {
      const supply = (!l.phantom && supplyOf?.(l.itemId)) || l.supply;
      return {
        key: l.key,
        depth: l.depth,
        itemId: l.itemId,
        sku: l.item.sku,
        name: l.item.name,
        perUnit: l.qty,
        supply,
        inside: l.phantom,
        packaging: l.packaging,
        cm: names.cm,
        from: supply === "hhl_consigns" && !l.phantom ? (names.fromOf?.(l.itemId) ?? null) : null,
      };
    });
}

/**
 * Who supplies a row of a build's parts, naming who (Mitch, 2026-11-04: not "You make it": "it would be Elecrow makes
 * it", and the joysticks "Hand Held Legend technically sends … but they are consigned from Favor Union"): the documents
 * page, the PDF, the portal and the PO say the same.
 */
export function buildSupplyText(row: Pick<BuildPartRow, "supply" | "inside" | "cm" | "from">, lang: "en" | "zh" = "en"): string {
  const cm = row.cm || (lang === "zh" ? "贵司" : "The CM");
  if (row.inside) return lang === "zh" ? `本次生产中由 ${cm} 组装` : `${cm} builds it in this order`;
  if (row.supply === "hhl_consigns")
    return lang === "zh"
      ? `由 Hand Held Legend 提供${row.from ? `（来自 ${row.from}）` : ""}`
      : `Hand Held Legend sends it${row.from ? ` (from ${row.from})` : ""}`;
  if (row.supply === "cm_buys") return lang === "zh" ? `由 ${cm} 采购` : `${cm} buys it`;
  return lang === "zh" ? `由 ${cm} 制作` : `${cm} makes it`;
}

/** A line's part_specs as stored (null until it's sent). */
export const partSpecsOf = (v: unknown): PartSpec[] | null => (Array.isArray(v) ? (v as PartSpec[]) : null);
